import React, { useState, useEffect } from 'react';
import { contentApi } from '../../services/api';
import Bearbeitungslog from '../logs/Bearbeitungslog';
import MeldungenSection from '../shared/MeldungenSection';

/**
 * Issue #410: Comment detail page.
 *
 * Reachable from the "Kommentare" list for comments that were automatically
 * deleted due to 5 or more reports. Mirrors the post/object detail layout
 * and shows the shared Meldungen section.
 */
function CommentDetail({ commentId, onBack }) {
  const [comment, setComment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreReason, setRestoreReason] = useState('');

  const fetchComment = async () => {
    try {
      const response = await contentApi.getComment(commentId);
      setComment(response.data);
    } catch (err) {
      setError('Fehler beim Laden des Kommentars');
      console.error('Comment fetch error:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchComment();
      setIsLoading(false);
    };
    loadData();
  }, [commentId]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isDeleted = () => comment?.deleted_at != null;
  const isAutoDeleted = () => isDeleted() && (comment?.report_count || 0) >= 5;

  const getStatusBadge = () => {
    if (isDeleted()) {
      return <span className="status-badge suspended">Gelöscht</span>;
    }
    return <span className="status-badge verified">Aktiv</span>;
  };

  const openRestoreModal = () => {
    setRestoreReason('');
    setShowRestoreModal(true);
  };

  const closeRestoreModal = () => {
    setShowRestoreModal(false);
    setRestoreReason('');
  };

  const handleRestore = async () => {
    if (!restoreReason.trim()) {
      setError('Bitte geben Sie einen Grund für die Wiederherstellung an');
      return;
    }

    setActionLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await contentApi.restoreComment(commentId, restoreReason.trim());
      setSuccessMessage('Kommentar wiederhergestellt');
      closeRestoreModal();
      await fetchComment();
    } catch (err) {
      if (err.response?.data?.error === 'not_deleted') {
        setError('Kommentar ist nicht gelöscht');
      } else if (err.response?.data?.error === 'reason_required') {
        setError('Bitte geben Sie einen Grund für die Wiederherstellung an');
      } else {
        setError('Wiederherstellen fehlgeschlagen: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setActionLoading(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!comment) {
    return (
      <div className="page-container">
        <div className="error-message">{error || 'Kommentar nicht gefunden'}</div>
        <button className="secondary-button" onClick={onBack}>Zurück</button>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <button className="secondary-button" onClick={onBack} style={{ marginBottom: '12px' }}>
            ← Zurück zur Liste
          </button>
          <h1>Kommentar Details</h1>
          <p className="page-subtitle">ID: {comment.id}</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Section 1: Comment Information */}
      <section className="detail-section">
        <div className="section-header">
          <h2>Kommentarinformationen</h2>
          {getStatusBadge()}
        </div>

        <div className="info-grid">
          <div className="info-item">
            <label>Erstellt am</label>
            <span>{formatDate(comment.created_at)}</span>
          </div>
          <div className="info-item">
            <label>Anonym</label>
            <span>{comment.is_anonymous ? <span className="anonym-checkmark">✓</span> : '-'}</span>
          </div>
        </div>

        <div className="info-item full-width" style={{ marginTop: '16px' }}>
          <label>Inhalt</label>
          <div className="content-preview">{comment.content}</div>
        </div>
      </section>

      {/* Section 2: Author Information */}
      <section className="detail-section">
        <div className="section-header">
          <h2>Autor</h2>
        </div>

        <div className="info-grid">
          <div className="info-item">
            <label>Name</label>
            <span>{comment.is_anonymous ? <em>Anonym</em> : comment.author_name}</span>
          </div>
          <div className="info-item">
            <label>E-Mail</label>
            <span>{comment.author_email}</span>
          </div>
          <div className="info-item">
            <label>Hochschule</label>
            <span>{comment.author_university || '-'}</span>
          </div>
        </div>
      </section>

      {/* Section 3: Engagement Metrics */}
      <section className="detail-section">
        <div className="section-header">
          <h2>Engagement</h2>
        </div>

        <div className="info-grid">
          <div className="info-item">
            <label>Meldungen</label>
            <span className={`metric-value ${(comment.report_count || 0) >= 5 ? 'warning' : ''}`}>
              {comment.report_count || 0}
            </span>
          </div>
        </div>
      </section>

      {/* Section 4: Deletion Info (if deleted) */}
      {isDeleted() && (
        <section className="detail-section warning-section">
          <div className="section-header">
            <h2>Löschung</h2>
          </div>

          <div className="info-grid">
            <div className="info-item">
              <label>Gelöscht am</label>
              <span>{formatDate(comment.deleted_at)}</span>
            </div>
            <div className="info-item">
              <label>Automatisch gelöscht</label>
              <span>
                {isAutoDeleted() ? (
                  <span className="anonym-checkmark">✓</span>
                ) : '-'}
              </span>
            </div>
          </div>
        </section>
      )}

      {/* Section 5: Actions (restore only when auto-deleted) */}
      {isAutoDeleted() && (
        <section className="detail-section">
          <div className="section-header">
            <h2>Aktionen</h2>
          </div>

          <div className="actions-grid">
            <div className="action-card">
              <h3>Kommentarverwaltung</h3>
              <div className="action-buttons-group">
                <button
                  className="action-btn activate"
                  onClick={openRestoreModal}
                  disabled={actionLoading}
                  title="Kommentar wiederherstellen"
                >
                  ↩ Wiederherstellen
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Issue #410: Meldungen section (shown only when reports exist) */}
      <MeldungenSection targetType="comment" targetId={commentId} />

      {/* Bearbeitungslog */}
      <Bearbeitungslog targetType="comment" targetId={commentId} />

      {/* Restore Reason Modal */}
      {showRestoreModal && (
        <div className="modal-overlay" onClick={closeRestoreModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Kommentar wiederherstellen</h2>
            <p className="modal-description">
              Bitte geben Sie einen Grund für die Wiederherstellung an.
              Dieser wird für Audit-Zwecke gespeichert.
            </p>
            <div className="form-group">
              <label htmlFor="restore-reason">Grund für Wiederherstellung *</label>
              <textarea
                id="restore-reason"
                value={restoreReason}
                onChange={(e) => setRestoreReason(e.target.value)}
                placeholder="Bitte geben Sie einen Grund an..."
                rows={4}
                className="modal-textarea"
                autoFocus
              />
            </div>
            <div className="modal-actions">
              <button
                className="secondary-button"
                onClick={closeRestoreModal}
                disabled={actionLoading}
              >
                Abbrechen
              </button>
              <button
                className="action-btn activate"
                onClick={handleRestore}
                disabled={actionLoading || !restoreReason.trim()}
              >
                {actionLoading ? 'Wird wiederhergestellt...' : 'Wiederherstellen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommentDetail;
